/* NFA text decrypt effect.
   Scrambles individual characters and resolves them independently whenever text enters the screen.
   No external libraries; respects reduced-motion. */
(function () {
  var cfg = window.NFA_SETTINGS || {};
  if (cfg.textDecrypt === false) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var DURATION = Number(cfg.decryptDuration) || 950;
  var SWAP_INTERVAL = 42;
  var CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  var observer;
  var tracked = new WeakSet();
  var state = new WeakMap();

  var EXCLUDED_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, SVG: 1, PATH: 1,
    INPUT: 1, TEXTAREA: 1, SELECT: 1, OPTION: 1, CODE: 1, PRE: 1
  };

  function isExcluded(el) {
    if (!el || el.nodeType !== 1) return true;
    if (EXCLUDED_TAGS[el.tagName]) return true;
    if (el.closest('[data-nfa-no-decrypt], .nfa-no-decrypt, .visually-hidden, [aria-hidden="true"], script, style, noscript, template, svg, code, pre')) return true;
    return false;
  }

  function eligibleTextNode(node) {
    if (!node || node.nodeType !== 3 || !node.parentElement) return false;
    if (isExcluded(node.parentElement)) return false;
    var text = node.nodeValue || '';
    if (!text.trim()) return false;
    return /[A-Za-z0-9]/.test(text);
  }

  function randomChar() {
    return CHARS.charAt(Math.floor(Math.random() * CHARS.length));
  }

  function animateNode(node) {
    if (!eligibleTextNode(node)) return;

    var current = state.get(node);
    if (current && current.raf) cancelAnimationFrame(current.raf);

    var original = current && current.original ? current.original : node.nodeValue;
    if (!/[A-Za-z0-9]/.test(original)) return;

    var positions = [];
    for (var i = 0; i < original.length; i++) {
      if (/[A-Za-z0-9]/.test(original.charAt(i))) positions.push(i);
    }
    if (!positions.length) return;

    // Each character gets its own independent resolve time so the word decrypts character-by-character.
    var resolveAt = {};
    positions.forEach(function (pos) {
      // Resolve most letters before the yellow wipe finishes, but leave a small tail
      // scrambling just past it so the effect is still visible after the transition.
      var r = Math.pow(Math.random(), 1.6);
      resolveAt[pos] = DURATION * (0.15 + r * 0.85);
    });

    var start = performance.now();
    var lastSwap = 0;
    var randomized = original.split('');
    var st = { original: original, raf: 0, lastRun: Date.now() };
    state.set(node, st);
    if (node.parentElement) node.parentElement.classList.add('nfa-decrypting');

    function frame(now) {
      var elapsed = now - start;
      if (elapsed - lastSwap > SWAP_INTERVAL) {
        lastSwap = elapsed;
        for (var p = 0; p < positions.length; p++) {
          var idx = positions[p];
          randomized[idx] = elapsed >= resolveAt[idx] ? original.charAt(idx) : randomChar();
        }
        node.nodeValue = randomized.join('');
      }

      if (elapsed < DURATION) {
        st.raf = requestAnimationFrame(frame);
      } else {
        node.nodeValue = original;
        st.raf = 0;
        if (node.parentElement) node.parentElement.classList.remove('nfa-decrypting');
      }
    }

    st.raf = requestAnimationFrame(frame);
  }

  function directTextNodes(el) {
    var out = [];
    if (!el || isExcluded(el)) return out;
    for (var i = 0; i < el.childNodes.length; i++) {
      if (eligibleTextNode(el.childNodes[i])) out.push(el.childNodes[i]);
    }
    return out;
  }

  function animateElement(el, force) {
    if (!el || isExcluded(el)) return;
    var nodes = directTextNodes(el);
    nodes.forEach(function (node) {
      var s = state.get(node);
      // Prevent duplicate triggers caused by layout settling, while still allowing re-entry/opening to decrypt again.
      if (!force && s && Date.now() - s.lastRun < 850) return;
      animateNode(node);
    });
  }

  function observeElement(el) {
    if (!el || tracked.has(el) || isExcluded(el)) return;
    if (!directTextNodes(el).length) return;
    tracked.add(el);
    if (observer) observer.observe(el);
  }

  function scan(root) {
    if (!root) return;
    if (root.nodeType === 1) observeElement(root);
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return eligibleTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    var node;
    var parents = [];
    while ((node = walker.nextNode())) {
      var p = node.parentElement;
      if (p && parents.indexOf(p) === -1) parents.push(p);
    }
    parents.forEach(observeElement);
  }

  function visibleNow(el) {
    if (!el || !el.getClientRects().length) return false;
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
  }

  function init() {
    if (!('IntersectionObserver' in window)) {
      scan(document.body);
      document.querySelectorAll('body *').forEach(function (el) {
        if (visibleNow(el)) animateElement(el, true);
      });
      return;
    }

    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        animateElement(entry.target, false);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -2% 0px' });

    scan(document.body);

    // Explicitly run the entrance text immediately, even though it is fixed above the normal page.
    var entrance = document.getElementById('nfaEntrance');
    if (entrance && !entrance.hidden) {
      entrance.querySelectorAll('.nfa-wordmark, .nfa-enter').forEach(function (el) {
        animateElement(el, true);
      });
    }

    // Re-run product-name decrypt whenever the mouse enters a product card.
    // relatedTarget keeps it from retriggering as the pointer moves between elements inside the same card.
    document.addEventListener('mouseover', function (e) {
      if (window.matchMedia && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      var card = e.target && e.target.closest ? e.target.closest('.product-card-wrapper') : null;
      if (!card) return;
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;

      card.querySelectorAll('.card__heading .card-title-text').forEach(function (el) {
        if (visibleNow(el)) animateElement(el, true);
      });
    }, true);

    // Re-run decrypt when drawers/accordions/dropdowns expose text.
    document.addEventListener('toggle', function (e) {
      var details = e.target;
      if (details && details.tagName === 'DETAILS' && details.open) {
        requestAnimationFrame(function () {
          scan(details);
          details.querySelectorAll('*').forEach(function (el) {
            if (visibleNow(el)) animateElement(el, true);
          });
        });
      }
    }, true);

    // Shopify and apps can inject sections after load; pick up newly added text automatically.
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          for (var i = 0; i < m.addedNodes.length; i++) {
            var added = m.addedNodes[i];
            if (added.nodeType === 1) scan(added);
          }
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
